import React from "react"
import PropTypes from "prop-types"
import { ResearchResult } from "@bw/partials"
import { Input, Checkbox } from "@bw/forms"
import { Formik, Form, Field } from "formik"
import * as Icons from "@bw/icons"
import styled from "styled-components"
import { breakpoints } from "../../../theme"
import { useTranslation } from "react-i18next"

const Wrapper = styled.div`
  background-color: #fff;
  border: rgba(0, 0, 0, 0.1) solid 1px;
  padding: 20px 25px;
  color: var(--text-color);
`
const Options = styled.div`
  display: flex;
  flex-direction: column-reverse;

  input {
    width: 100%;
  }

  > * {
    margin-bottom: 20px;
  }

  @media (min-width: ${breakpoints.small}px) {
    flex-direction: row;

    > :nth-child(2) {
      margin-left: 20px;
    }
  }
`

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;

  > * {
    margin-bottom: 20px;
  }
`
const Reset = styled.button`
  border: rgba(0, 0, 0, 0.1) solid 1px;
  border-radius: 6px;
  background: white;
  width: fit-content;
  padding: 5px 10px;
  cursor: pointer;
  font-size: 14px;
  @media (min-width: ${breakpoints.small}px) {
    margin-left: 20px;
  }
`

const Result = styled.div`
  &:not(:nth-last-of-type(1)) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }
`

const SeeAll = styled.button`
  display: flex;
  align-items: center;
  color: var(--primary);
  background: none;
  border: none;
  padding: 0;
  font-weight: bold;
  cursor: pointer;
  margin: 25px 0;
  width: 100%;
  padding-top: 15px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  > span {
    margin-right: 10px;
  }
`
const initialValues = {
  search: "",
  collection: [],
}

const Research = ({ results, history, filters, onFilter, onSelect }) => {
  const { t } = useTranslation()
  const [seeAll, setSeeAll] = React.useState(false)

  return (
    <Wrapper>
      <Formik initialValues={initialValues} onSubmit={onFilter}>
        {({ values, submitForm, handleReset }) => (
          <Form
            onChange={e => {
              setSeeAll(false)
              submitForm()
            }}
          >
            <Options>
              <Field
                name="search"
                id="search-input"
                component={Input}
                autoComplete="off"
                icon={<Icons.Search />}
                placeholder={t("menu.research")}
              />
              <Reset
                onClick={() => {
                  setSeeAll(false)
                  handleReset()
                }}
              >
                {t("search.reset")}
              </Reset>
            </Options>
            <Filters>
              {filters &&
                filters.map((filter, i) => (
                  <Field
                    key={i}
                    component={Checkbox}
                    checked={values.collection.includes(filter.value)}
                    label={filter.title}
                    name="collection"
                    value={filter.value}
                  />
                ))}
            </Filters>
            {results.length > 0 && (
              <>
                {results
                  .slice(0, seeAll ? results.length : 3)
                  .map((result, i) => (
                    <Result key={i}>
                      <ResearchResult
                        to={result.to}
                        type={result.type}
                        title={result.title}
                        excerpt={result.excerpt}
                        onClick={() => {
                          let history =
                            localStorage.getItem("search-history") || "[]"
                          history = JSON.parse(history)

                          // dont add the same result to history twice
                          if (!history.includes(result.to)) {
                            history.unshift(result.to)
                            localStorage.setItem(
                              "search-history",
                              JSON.stringify(history.slice(0, 3))
                            )
                          }
                          onSelect()
                        }}
                      />
                    </Result>
                  ))}
                {results.length > 3 && !seeAll && (
                  <SeeAll
                    onClick={() => {
                      setSeeAll(true)
                    }}
                  >
                    <span>{t("search.seeAll", { count: results.length })}</span>
                    <Icons.ArrowMenu />
                  </SeeAll>
                )}
              </>
            )}
            {history.length > 0 && values.search.length < 2 && (
              <>
                <h3 style={{ margin: "30px 0 0 " }}>{t("search.history")}</h3>
                {history.slice(0, 3).map((history, i) => (
                  <Result key={i}>
                    <ResearchResult
                      onClick={onSelect}
                      to={history.to}
                      type={history.type}
                      title={history.title}
                      excerpt={history.excerpt}
                    />
                  </Result>
                ))}
              </>
            )}
            {results.length === 0 && values.search.length >= 2 && (
              <div>
                <h3 style={{ margin: "30px 0 15px 0" }}>
                  {t("search.noResult")}
                </h3>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </Wrapper>
  )
}

Research.propTypes = {
  onFilter: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  results: PropTypes.arrayOf(
    PropTypes.shape({
      to: PropTypes.string,
      image: PropTypes.string,
      title: PropTypes.string,
      excerpt: PropTypes.string,
    })
  ),
  history: PropTypes.arrayOf(
    PropTypes.shape({
      to: PropTypes.string,
      image: PropTypes.string,
      title: PropTypes.string,
      excerpt: PropTypes.string,
    })
  ),
}

Research.defaultProps = {
  results: [],
  history: [],
  filters: [],
}

export { Research }
